import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, type PhoneMetrics } from "../renderConstants";

export interface PhoneWalletDeps {
  drawNotificationPanel(
    rect: Rect,
    opts: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number },
  ): void;
  drawNotificationChip(
    rect: Rect, label: string, accent: string, fontPx: number,
  ): void;
  drawCharacterPortrait(path: string, x: number, y: number, w: number, h: number): void;
  drawCoinIcon(x: number, y: number, size: number): void;
  drawGemIcon(x: number, y: number, size: number): void;
}

export function drawPhoneWalletScreen(
  deps: PhoneWalletDeps,
  ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, _scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;
  ctx.fillStyle = "#a78bfa";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("WALLET", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  const btnH = metrics.ctaH;
  const cardH = desktop ? 32 : 36;
  const iconSize = desktop ? 16 : 20;

  // ── Gems balance ──
  if (desktop) {
    deps.drawNotificationPanel(
      { x: contentX, y: curY, w: contentW, h: cardH },
      { fill: "rgba(16,29,49,0.95)", stroke: "rgba(255,215,0,0.3)", accent: "#ffd700", radius: metrics.cardRadius }
    );
  } else {
    ctx.fillStyle = "#162030";
    ctx.fillRect(contentX, curY, contentW, cardH);
    ctx.fillStyle = "#ffd700";
    ctx.fillRect(contentX, curY, 3, cardH);
  }
  // Gem icon + label
  const gemIconX = contentX + 10;
  const gemIconY = curY + (cardH - iconSize) / 2;
  deps.drawGemIcon(gemIconX, gemIconY, iconSize);
  ctx.fillStyle = "#ffd700";
  ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("GEMS", gemIconX + iconSize + 6, curY + cardH / 2);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${desktop ? metrics.valueFontPx + 3 : 12}px ${GAME_FONT}`;
  ctx.textAlign = "right";
  ctx.fillText(String(model.gems), contentRight - 8, curY + cardH / 2);
  curY += cardH + metrics.rowGap;

  // ── Coins balance ──
  if (desktop) {
    deps.drawNotificationPanel(
      { x: contentX, y: curY, w: contentW, h: cardH },
      { fill: "rgba(16,29,49,0.95)", stroke: "rgba(100,216,255,0.3)", accent: "#64d8ff", radius: metrics.cardRadius }
    );
  } else {
    ctx.fillStyle = "#162030";
    ctx.fillRect(contentX, curY, contentW, cardH);
    ctx.fillStyle = "#64d8ff";
    ctx.fillRect(contentX, curY, 3, cardH);
  }
  // Coin icon + label
  const coinIconX = contentX + 10;
  const coinIconY = curY + (cardH - iconSize) / 2;
  deps.drawCoinIcon(coinIconX, coinIconY, iconSize);
  ctx.fillStyle = "#64d8ff";
  ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("COINS", coinIconX + iconSize + 6, curY + cardH / 2);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${desktop ? metrics.valueFontPx + 3 : 12}px ${GAME_FONT}`;
  ctx.textAlign = "right";
  ctx.fillText(String(model.coins), contentRight - 8, curY + cardH / 2);
  curY += cardH + metrics.sectionGap;

  // ── View Skins button ──
  const skinsRect: Rect = { x: contentX, y: curY, w: contentW, h: btnH };
  uiRects.walletViewSkins = skinsRect;
  if (desktop) {
    deps.drawNotificationPanel(skinsRect, { fill: "#1a1a30", stroke: "#ff6b9d", radius: 4 });
  } else {
    ctx.fillStyle = "#1a1a30";
    ctx.fillRect(skinsRect.x, skinsRect.y, skinsRect.w, skinsRect.h);
    ctx.strokeStyle = "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(skinsRect.x, skinsRect.y, skinsRect.w, skinsRect.h);
  }
  ctx.fillStyle = "#ff6b9d";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("VIEW SKINS", skinsRect.x + skinsRect.w / 2, skinsRect.y + skinsRect.h / 2);
  curY += btnH + metrics.sectionGap;

  // ── ETH Wallet Section ──
  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("ETH WALLET", scrX + scrW / 2, curY);
  curY += desktop ? 12 : 14;

  if (model.walletConnecting) {
    ctx.fillStyle = "#ffd700";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.fillText("CONNECTING...", scrX + scrW / 2, curY + 4);
    curY += desktop ? 24 : 24;
  } else if (model.walletAddress) {
    // Connected: show address + sync + unlink
    const walletCardH = desktop ? 30 : 28;
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: walletCardH },
        { fill: "rgba(16,29,49,0.95)", stroke: "rgba(100,216,255,0.3)", accent: "#64d8ff", radius: metrics.cardRadius }
      );
    } else {
      ctx.fillStyle = "#162030";
      ctx.fillRect(contentX, curY, contentW, walletCardH);
      ctx.fillStyle = "#64d8ff";
      ctx.fillRect(contentX, curY, 3, walletCardH);
    }
    ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const addr = model.walletAddress;
    ctx.fillStyle = "#c8d8f0";
    ctx.fillText(`${addr.slice(0, 6)}...${addr.slice(-4)}`, contentX + 10, curY + (desktop ? 10 : 10));
    ctx.fillStyle = model.nftCacheStale ? "#ffcc44" : "#4dff88";
    ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.fillText(model.nftSyncAge, contentX + 10, curY + (desktop ? 21 : 20));
    if (desktop) {
      deps.drawNotificationChip(
        { x: contentRight - 60, y: curY + 8, w: 52, h: 12 },
        model.nftCacheStale ? "STALE" : "READY",
        model.nftCacheStale ? "#ffcc44" : "#4dff88",
        metrics.helperFontPx
      );
    }
    curY += walletCardH + metrics.rowGap;

    // Sync + Unlink buttons side by side
    const halfW = (contentW - 4) / 2;
    const syncRect: Rect = { x: contentX, y: curY, w: halfW, h: btnH };
    uiRects.walletSync = syncRect;
    if (desktop) {
      deps.drawNotificationPanel(syncRect, { fill: "#1a2a40", stroke: model.nftCacheStale ? "#ffcc44" : "#64d8ff", radius: 4 });
    } else {
      ctx.fillStyle = "#1a2a40";
      ctx.fillRect(syncRect.x, syncRect.y, syncRect.w, syncRect.h);
      ctx.strokeStyle = model.nftCacheStale ? "#ffcc44" : "#64d8ff";
      ctx.lineWidth = 1;
      ctx.strokeRect(syncRect.x, syncRect.y, syncRect.w, syncRect.h);
    }
    ctx.fillStyle = model.nftCacheStale ? "#ffcc44" : "#64d8ff";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(model.nftSyncing ? "SYNCING..." : "SYNC", syncRect.x + syncRect.w / 2, syncRect.y + syncRect.h / 2);

    const unlinkRect: Rect = { x: contentX + halfW + 4, y: curY, w: halfW, h: btnH };
    uiRects.walletUnlink = unlinkRect;
    if (desktop) {
      deps.drawNotificationPanel(unlinkRect, { fill: "#2a1010", stroke: "#ff6644", radius: 4 });
    } else {
      ctx.fillStyle = "#2a1010";
      ctx.fillRect(unlinkRect.x, unlinkRect.y, unlinkRect.w, unlinkRect.h);
      ctx.strokeStyle = "#ff6644";
      ctx.strokeRect(unlinkRect.x, unlinkRect.y, unlinkRect.w, unlinkRect.h);
    }
    ctx.fillStyle = "#ff6644";
    ctx.fillText("UNLINK", unlinkRect.x + unlinkRect.w / 2, unlinkRect.y + unlinkRect.h / 2);
    curY += btnH + metrics.sectionGap;
  } else {
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: 30 },
        { fill: "rgba(16,29,49,0.95)", stroke: "rgba(112,96,255,0.28)", accent: "#7060ff", radius: metrics.cardRadius }
      );
      ctx.fillStyle = "#c8d8f0";
      ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText("NOT CONNECTED", contentX + 10, curY + 10);
      ctx.fillStyle = "#7ea0c7";
      ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.fillText("LINK TO SYNC NFT SKINS", contentX + 10, curY + 21);
      curY += 30 + metrics.rowGap;
    }
    // Connect button
    const connectRect: Rect = { x: contentX, y: curY, w: contentW, h: btnH };
    uiRects.walletConnect = connectRect;
    if (desktop) {
      deps.drawNotificationPanel(connectRect, { fill: "#1a2040", stroke: "#7060ff", radius: 4 });
    } else {
      ctx.fillStyle = "#1a2040";
      ctx.fillRect(connectRect.x, connectRect.y, connectRect.w, connectRect.h);
      ctx.strokeStyle = "#7060ff";
      ctx.lineWidth = 1;
      ctx.strokeRect(connectRect.x, connectRect.y, connectRect.w, connectRect.h);
    }
    ctx.fillStyle = "#7060ff";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("CONNECT ETHEREUM", connectRect.x + connectRect.w / 2, connectRect.y + connectRect.h / 2);
    curY += btnH + metrics.sectionGap;
  }

  // ── SOL Wallet Section ──
  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("SOLANA WALLET", scrX + scrW / 2, curY);
  curY += desktop ? 12 : 14;

  if (model.solWalletConnected && model.solWalletAddress) {
    // Connected: show address + unlink
    const solCardH = desktop ? 30 : 28;
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: solCardH },
        { fill: "#0a1c14", stroke: "rgba(20,241,149,0.3)", accent: "#14f195", radius: metrics.cardRadius }
      );
    } else {
      ctx.fillStyle = "#0a1c14";
      ctx.fillRect(contentX, curY, contentW, solCardH);
      ctx.fillStyle = "#14f195";
      ctx.fillRect(contentX, curY, 3, solCardH);
    }
    const sAddr = model.solWalletAddress;
    ctx.fillStyle = "#c8d8f0";
    ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(`${sAddr.slice(0, 6)}...${sAddr.slice(-4)}`, contentX + 10, curY + (desktop ? 10 : 12));
    if (desktop) {
      ctx.fillStyle = "#7ce5d8";
      ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.fillText("CONNECTED FOR TOKEN SHOP", contentX + 10, curY + 21);
    }
    curY += solCardH + metrics.rowGap;

    const disconnRect: Rect = { x: contentX, y: curY, w: contentW, h: btnH };
    uiRects.solWalletDisconnect = disconnRect;
    if (desktop) {
      deps.drawNotificationPanel(disconnRect, { fill: "#2a1010", stroke: "#ff6644", radius: 4 });
    } else {
      ctx.fillStyle = "#2a1010";
      ctx.fillRect(disconnRect.x, disconnRect.y, disconnRect.w, disconnRect.h);
      ctx.strokeStyle = "#ff6644";
      ctx.lineWidth = 1;
      ctx.strokeRect(disconnRect.x, disconnRect.y, disconnRect.w, disconnRect.h);
    }
    ctx.fillStyle = "#ff6644";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("DISCONNECT", disconnRect.x + disconnRect.w / 2, disconnRect.y + disconnRect.h / 2);
    curY += btnH + metrics.sectionGap;
  } else {
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: 30 },
        { fill: "#0a1c14", stroke: "rgba(20,241,149,0.24)", accent: "#14f195", radius: metrics.cardRadius }
      );
      ctx.fillStyle = "#c8d8f0";
      ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText("NOT CONNECTED", contentX + 10, curY + 10);
      ctx.fillStyle = "#7ce5d8";
      ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
      ctx.fillText("LINK TO ACCESS SOL FEATURES", contentX + 10, curY + 21);
      curY += 30 + metrics.rowGap;
    }
    const solConnRect: Rect = { x: contentX, y: curY, w: contentW, h: btnH };
    uiRects.solWalletConnect = solConnRect;
    if (desktop) {
      deps.drawNotificationPanel(solConnRect, { fill: "#0a1c14", stroke: "#14f195", radius: 4 });
    } else {
      ctx.fillStyle = "#0a1c14";
      ctx.fillRect(solConnRect.x, solConnRect.y, solConnRect.w, solConnRect.h);
      ctx.strokeStyle = "#14f195";
      ctx.lineWidth = 1;
      ctx.strokeRect(solConnRect.x, solConnRect.y, solConnRect.w, solConnRect.h);
    }
    ctx.fillStyle = "#14f195";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("CONNECT SOLANA", solConnRect.x + solConnRect.w / 2, solConnRect.y + solConnRect.h / 2);
    curY += btnH + metrics.sectionGap;
  }
}
